import {
  ContactAndTravelerInformationState,
  ContactAndTravelerInformationChildState,
} from "@apac/checkout";
import type { PartialParentContext } from "@checkout/index";
import { ReviewGuardTypes } from "@checkout/states";
import type { NextEventType } from "@checkout/states/common/ContactInformation/events";
import { GuardTypes as TravelerGuardTypes } from "@checkout/states/common/TravelerInformation/guards";
import { trackAddContactEvent } from "@checkout/tracking/contactInformation/trackAddContactEvent";
import { trackEditTravelers } from "@checkout/tracking/passengerInformation/trackEditTravelers";
import { trackNewTraveler } from "@checkout/tracking/passengerInformation/trackNewTraveler";

interface StateOverrideConfig {
  nextState: string;
  nextStateFromReview?: string;
}

/**
 * RBC-specific override for the ContactAndTraveler state machine.
 *
 * This fixes RBC-1580: Focus loss and slow "Save and continue" issues.
 *
 * The APAC default uses `invoke` for tracking which waits for promises to resolve:
 * - In travelerForm states (new/edit): causes focus loss after ~2s
 * - In saving state: delays transition to cart quote until tracking completes
 *
 * Our override uses `entry` actions to fire tracking asynchronously without
 * blocking, then immediately transitions to the next state.
 */
export const getRbcContactAndTravelerStateOverride = (
  config: StateOverrideConfig
) => ({
  [ContactAndTravelerInformationState.travelerForm]: {
    initial: ContactAndTravelerInformationChildState.route,
    states: {
      [ContactAndTravelerInformationChildState.route]: {
        always: [
          {
            cond: TravelerGuardTypes.hasCurrentTraveler,
            target: ContactAndTravelerInformationChildState.edit,
          },
          {
            target: ContactAndTravelerInformationChildState.new,
          },
        ],
      },
      [ContactAndTravelerInformationChildState.edit]: {
        entry: (context: PartialParentContext) => {
          // Fire-and-forget tracking - don't block the state machine
          // trackEditTravelers has built-in error handling (see trackBaseEvent)
          trackEditTravelers(context);
        },
        always: { target: ContactAndTravelerInformationChildState.idle },
      },
      [ContactAndTravelerInformationChildState.new]: {
        entry: (context: PartialParentContext) => {
          // Fire-and-forget tracking - don't block the state machine
          // trackNewTraveler has built-in error handling (see trackBaseEvent)
          trackNewTraveler(context);
        },
        always: { target: ContactAndTravelerInformationChildState.idle },
      },
      [ContactAndTravelerInformationChildState.idle]: {},
      // update state will be preserved from APAC defaults via deep merge
    },
  },
  [ContactAndTravelerInformationState.saving]: {
    entry: (context: PartialParentContext, event: NextEventType) => {
      // Fire-and-forget tracking - don't block the transition to cart quote
      // trackAddContactEvent has built-in error handling (see trackBaseEvent)
      trackAddContactEvent(context, event);
    },
    always: [
      {
        cond: ReviewGuardTypes.visitedReview,
        target: config.nextStateFromReview || config.nextState,
      },
      {
        target: config.nextState,
      },
    ],
  },
});
